import { useCallback, useMemo, useRef, useState } from 'react';
import { useField } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import imageStreamReaderAsync from 'utils/imageStreamReaderAsync';
import getBase64String from 'utils/getBase64String';
import generateUUIDv4 from 'utils/generateUUIDv4';
import List from '@mui/material/List';
import { FormAttachment } from 'types/appointment';
import { AppointmentAttachmentItem } from './AppointmentAttachmentItem';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Theme } from '@mui/material/styles';

const boxStyles = {
    position: 'relative',
    backgroundColor: 'grey.300',
    maxWidth: '90vw',
    margin: 'auto',

    '& img': {
        maxWidth: '100%',
    },
};

const boxWrapper = (theme: Theme) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'auto',
    p: 5,
    [theme.breakpoints.only('xs')]: {
        p: 2,
    },
});

export const AppointmentAttachmentSection = () => {
    const [field, meta, helpers] = useField<FormAttachment[]>('attachments');
    const [image, setImage] = useState<{ name: string; url: string } | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const fileInputClicked = () => {
        if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleChangeSelected = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                const files = Object.values(e.target.files);
                Promise.all(
                    files.map((file) => imageStreamReaderAsync(file).then((result) => ({ result, file }))),
                ).then((res) => {
                    const addedFiles = res.map((f) => {
                        const base64 = getBase64String(f.result as string);
                        return {
                            id: generateUUIDv4('local-'),
                            image: base64,
                            fileName: f.file.name,
                            file: f.file,
                        };
                    });

                    field.onChange({ target: { name: 'attachments', value: field.value.concat(addedFiles) } });
                });
            }
        },
        [field],
    );

    const deleteImageHandler = useCallback(
        (id: string) => {
            const updatedList = field.value.filter((item) => item.id !== id);
            helpers.setValue(updatedList);
        },
        [field, helpers],
    );

    const viewImageHandler = useCallback(
        (id: string) => {
            const info = field.value.find((item) => item.id === id);
            if (!info) {
                return;
            }
            if (info?.file) {
                imageStreamReaderAsync(info.file).then((res) => {
                    setImage({ name: info.fileName, url: res as string });
                });
                return;
            }
            setImage({ name: info.fileName, url: info.filePath as string });
        },
        [field, helpers],
    );

    const handleClose = useCallback(() => {
        setImage(null);
    }, []);

    return (
        <Box>
            <Button variant="outlined" onClick={fileInputClicked}>
                Add Attachments
            </Button>
            <Box
                component="input"
                ref={fileInputRef}
                accept=".jpeg, .png, .jpg"
                type="file"
                display="none"
                multiple
                onChange={handleChangeSelected}
            />
            <List>
                {field.value.map((imageInfo, index) => (
                    <AppointmentAttachmentItem
                        key={imageInfo.id}
                        {...imageInfo}
                        error={(meta.error?.[index] as unknown as { file: string })?.file}
                        onDelete={deleteImageHandler}
                        onView={viewImageHandler}
                    />
                ))}
            </List>
            {image && (
                <Modal
                    open={!!image}
                    onClose={handleClose}
                    aria-labelledby="image-modal-title"
                    aria-describedby="image-modal-description"
                >
                    <Box sx={boxWrapper}>
                        <Box sx={boxStyles}>
                            <Box
                                width="100%"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                pl={2}
                                pr={1}
                            >
                                <Typography id="image-modal-title" variant="h6" component="h2">
                                    {image.name}
                                </Typography>
                                <IconButton onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <img src={image.url} alt="image" />
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
};
